import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Paper, Typography } from '@mui/material';
import { AutoFixHigh } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { type AssetLevelInputType } from './types/input-steps.types';
import { validateAssetsLevelInput } from './utils/input-validators';
import { AssetLevelInput } from './components/AssetLevelInput';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import FleetPlanningHandler from 'handlers/fleet-planning.handler';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import {
  setAssetLevelInput,
  setFleetLevelInput,
  setPlanTimeWindow,
} from 'store/fleet-planning.slice';
import {
  selectActiveLoadingPoints,
  selectActiveDumpingPoints,
} from 'store/site-config.slice';
import { setMakeModelDetails } from 'store/make-model-details.slice';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import useCustomerInfo from 'hooks/useCustomerInfo';
import {
  getCustomerShiftsUTCForDate,
  getCurrentCustomerShiftInfo,
  resolveShiftOptions,
} from 'utils/helpers/customerShiftHelpers';
import { useTranslation } from 'react-i18next';
import useAppTheme from 'hooks/useAppTheme';
import MakeModelAPI from 'api/makeModel.api';

dayjs.extend(utc);
dayjs.extend(timezone);

export const InputStep = ({
  setCurrentStep,
  shiftWise,
}: {
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  shiftWise: boolean;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const { customerTz, customerCode } = useCustomerInfo();
  const fleetPlanningHandler = new FleetPlanningHandler();
  const makeModelApi = new MakeModelAPI();

  const assetsData = useAppSelector((state) => state.assetReducer.assets);
  const latestTagData = useAppSelector(
    (state) => state.deviceReducer.latestTagData
  );
  const reduxFleetLevelInput = useAppSelector(
    (state) => state.fleetPlanningReducer.fleetLevelInput
  );
  const reduxAssetsLevelInput = useAppSelector(
    (state) => state.fleetPlanningReducer.assetLevelInput
  );
  const isLoading = useAppSelector(
    (state) => state.fleetPlanningReducer.isLoading
  );

  const activeLoadingPoints = useAppSelector(selectActiveLoadingPoints);
  const activeDumpingPoints = useAppSelector(selectActiveDumpingPoints);
  const minimalFleetCount = useAppSelector(
    (state) => state.siteConfigReducer.minimalFleetCount
  );
  const availableGrades = useAppSelector(
    (state) => state.siteConfigReducer.availableGrades ?? []
  );
  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const makeModelDetails = useAppSelector(
    (state) => state.makeModelDetailsReducer
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (makeModelDetails.length > 0 || !customerId) return;
    makeModelApi
      .getMakeModelDetails(customerId)
      .then((data) => {
        dispatch(setMakeModelDetails(data));
      })
      .catch(() => {});
  }, [customerId]); // eslint-disable-line

  const [assetLevelInputData, setAssetLevelInputData] = useState<
    AssetLevelInputType[]
  >([]);
  const [assetErrors, setAssetErrors] = useState<
    Record<string, Record<string, string>>
  >({});
  const [siteError, setSiteError] = useState<string | null>(null);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(
    new Set()
  );
  const [selectedShiftNumber, setSelectedShiftNumber] = useState<number | null>(
    () => {
      const { currentShiftNumber } = getCurrentCustomerShiftInfo(
        customerCode,
        customerTz
      );
      return currentShiftNumber ?? 1;
    }
  );

  const labelColor = enableDarkTheme
    ? 'rgba(148,163,184,0.8)'
    : 'rgba(0,0,0,0.45)';

  const ALL_AVAILABLE_ASSETS = useMemo(() => {
    if (!Array.isArray(assetsData)) return [];

    const customerKey = customerCode?.toLowerCase() ?? 'others';
    const baseDate = dayjs().tz(customerTz).startOf('day');
    const dateStr = baseDate.format('YYYY-MM-DD');

    const getStartEndHours = () => {
      if (shiftWise) {
        const shifts = getCustomerShiftsUTCForDate(customerKey, dateStr);
        if (!Array.isArray(shifts) || shifts.length === 0) {
          const fallback = resolveShiftOptions([], customerKey)[0];
          return { startHour: fallback.startHour, endHour: fallback.endHour };
        }
        const { currentShiftNumber } = getCurrentCustomerShiftInfo(
          customerCode,
          customerTz
        );
        const idx = (currentShiftNumber ?? 1) - 1;
        const shift = shifts[idx] ?? shifts[0];
        return { startHour: shift.startHour, endHour: shift.endHour };
      }
      const defaultShift = resolveShiftOptions([], customerKey)[0];
      return {
        startHour: defaultShift.startHour,
        endHour: defaultShift.endHour,
      };
    };

    const { startHour, endHour } = getStartEndHours();

    return assetsData
      .filter((el) => el?.assetType === 'haul_truck')
      .map((el) => {
        let startTime: dayjs.Dayjs;
        let endTime: dayjs.Dayjs;

        if (shiftWise) {
          const normalizedEndHour =
            endHour <= startHour ? endHour + 24 : endHour;
          const customerDayStart = dayjs.tz(`${dateStr}T00:00:00`, customerTz);
          startTime = customerDayStart.add(startHour, 'hour');
          endTime = customerDayStart.add(normalizedEndHour, 'hour');
        } else {
          startTime = baseDate.hour(startHour).minute(0);
          endTime = baseDate.hour(endHour).minute(0);
        }

        const shiftDuration = endTime.diff(startTime, 'minutes');
        const lunchStart = startTime.add(shiftDuration / 2, 'minutes');
        const lunchEnd = lunchStart.add(60, 'minutes');

        const makeModelMatch = makeModelDetails.find(
          (mm) => mm.make === el.make && mm.model === el.model
        );
        const defaultCapacity = customerKey === 'drn' ? '37' : '85';
        const capacity =
          makeModelMatch?.current_capacity != null
            ? String(makeModelMatch.current_capacity)
            : makeModelMatch?.max_payload_capacity != null
            ? String(makeModelMatch.max_payload_capacity)
            : defaultCapacity;

        return {
          id: el?.assetId,
          name: el?.bumperNumber,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          capacity,
          lunchStart: lunchStart.toISOString(),
          lunchEnd: lunchEnd.toISOString(),
          lunchBreakMinutes: '60',
          isActive: el?.isActive ?? false,
          lastLocation: el?.device?.last_location ?? null,
          lastLocationTs: el?.device?.last_location_ts ?? null,
          lastLat:
            latestTagData?.[el?.device?.deviceId]?.gps_latitude != null
              ? Number(latestTagData[el.device.deviceId].gps_latitude)
              : null,
          lastLng:
            latestTagData?.[el?.device?.deviceId]?.gps_longitude != null
              ? Number(latestTagData[el.device.deviceId].gps_longitude)
              : null,
        };
      });
  }, [
    assetsData,
    customerCode,
    customerTz,
    shiftWise,
    latestTagData,
    makeModelDetails,
  ]);

  const handleClickNext = async () => {
    try {
      if (
        activeLoadingPoints.length === 0 ||
        activeDumpingPoints.length === 0
      ) {
        setSiteError(
          activeLoadingPoints.length === 0 && activeDumpingPoints.length === 0
            ? 'At least one loading site and one dumping site must be configured.'
            : activeLoadingPoints.length === 0
            ? 'At least one loading site must be configured.'
            : 'At least one dumping site must be configured.'
        );
        return;
      }
      setSiteError(null);

      const assetErrs = validateAssetsLevelInput(assetLevelInputData);
      setAssetErrors(assetErrs);
      if (Object.keys(assetErrs).length > 0) return;

      const grades = activeLoadingPoints
        .map((lp) => {
          if (lp.gradeId) {
            return {
              gradeId: lp.gradeId,
              materialGrade: lp.materialGradeValue ?? 0,
              loadingCapacity: lp.loadingCapacity,
            };
          }
          const match = availableGrades.find((g) => g.locationName === lp.name);
          if (match) {
            return {
              gradeId: match.gradeId,
              materialGrade:
                match.qualities?.CU != null ? Number(match.qualities.CU) : 0,
              loadingCapacity: lp.loadingCapacity,
            };
          }
          return null;
        })
        .filter(
          (
            g
          ): g is {
            gradeId: string;
            materialGrade: number;
            loadingCapacity: number;
          } => g !== null
        );

      const dumpingSites = activeDumpingPoints
        .filter((dp) => dp.locationId != null)
        .map((dp) => ({
          locationId: dp.locationId as string,
          targetBlend:
            dp.targetBlend != null && dp.targetBlend > 0
              ? dp.targetBlend / 100
              : 0,
          targetBlendTolerance: 0.05,
          crusherRate: dp.dumpingCapacity,
        }));

      const firstLoadingLocationId =
        activeLoadingPoints[0]?.gradeLocationId ??
        availableGrades.find(
          (g) => g.locationName === activeLoadingPoints[0]?.name
        )?.locationId ??
        null;

      const trucks = assetLevelInputData.map((asset) => {
        const hasLatLng = asset.lastLat != null && asset.lastLng != null;
        return {
          assetId: asset.name,
          ...(hasLatLng
            ? { lat: asset.lastLat, lon: asset.lastLng }
            : firstLoadingLocationId != null
            ? { startLocationId: firstLoadingLocationId }
            : {}),
          capacity: Math.round(Number(asset.capacity)),
          startTime: dayjs(asset.startTime).utc().toISOString(),
          endTime: dayjs(asset.endTime).utc().toISOString(),
          lunchWindowStart: dayjs(asset.lunchStart).utc().toISOString(),
          lunchWindowEnd: dayjs(asset.lunchEnd).utc().toISOString(),
          lunchWindowBreakMinutes: Number(asset.lunchBreakMinutes ?? '60'),
        };
      });

      dispatch(setFleetLevelInput(reduxFleetLevelInput));
      dispatch(setAssetLevelInput(assetLevelInputData));
      dispatch(
        setPlanTimeWindow({
          startTime: trucks.reduce(
            (min, t) => (t.startTime < min ? t.startTime : min),
            trucks[0].startTime
          ),
          endTime: trucks.reduce(
            (max, t) => (t.endTime > max ? t.endTime : max),
            trucks[0].endTime
          ),
        })
      );

      await fleetPlanningHandler.createDispatchPlanV3({
        customerId,
        grades,
        dumpingSites,
        trucks,
        fleetLevelInput: {
          excessIdling: Number(reduxFleetLevelInput.excessIdling),
          lunch_window_tolerance_minutes: Number(
            reduxFleetLevelInput.lunchTolerance
          ),
        },
      });
      setCurrentStep(3);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (reduxAssetsLevelInput.length > 0) {
      setAssetLevelInputData(reduxAssetsLevelInput);
    }
  }, [reduxAssetsLevelInput]);

  const totalDumpCapacity = activeDumpingPoints.reduce(
    (sum, dp) => sum + (dp.dumpingCapacity ?? 0),
    0
  );

  // Trucks needed: prefer API result from get-minimal-fleet-count, else capacity-based estimate
  const capacityEstimate =
    activeDumpingPoints.length === 0
      ? 0
      : totalDumpCapacity > 0
      ? Math.max(
          activeDumpingPoints.length * 2,
          Math.ceil(totalDumpCapacity / 50)
        )
      : activeDumpingPoints.length * 2;
  const estimatedTrucksNeeded = minimalFleetCount ?? capacityEstimate;

  const selectedAssetsCount = assetLevelInputData.length;
  const hasMismatch =
    selectedAssetsCount > 0 && selectedAssetsCount < estimatedTrucksNeeded;

  return (
    <>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            backgroundColor: 'customBackground.section',
          }}
        >
          <SingleFadeLoader
            isFullScreen={false}
            message={t('creating_plan_please_wait')}
          />
        </Box>
      ) : (
        <Box
          height="100%"
          maxHeight="100%"
          overflow="auto"
          display="flex"
          flexDirection="column"
          gap={2}
        >
          {/* Header */}
          <Box
            component="header"
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{ fontWeight: 500, fontSize: '15px', color: 'text.primary' }}
            >
              {t('cdp_input_title')}
            </Typography>
            <CustomLoadingButton
              variant="contained"
              onClick={handleClickNext}
              disabled={assetLevelInputData.length === 0}
            >
              {t('create_plan')}
            </CustomLoadingButton>
          </Box>

          {/* ── ALGORITHM RECOMMENDATION PANEL ── */}
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 1.5,
              background: enableDarkTheme
                ? 'linear-gradient(135deg, rgba(168,85,247,0.12) 0%, rgba(59,130,246,0.08) 100%)'
                : 'linear-gradient(135deg, rgba(168,85,247,0.06) 0%, rgba(59,130,246,0.04) 100%)',
              border: `1px solid ${
                enableDarkTheme
                  ? 'rgba(168,85,247,0.4)'
                  : 'rgba(168,85,247,0.25)'
              }`,
              borderRadius: 2,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
            >
              {/* Left: title + description */}
              <Box display="flex" alignItems="center" gap={1}>
                <AutoFixHigh sx={{ color: '#c084fc', fontSize: 18 }} />
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 13,
                      color: 'text.primary',
                    }}
                  >
                    {t('cdp_algorithm_recommendation')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    {t('cdp_algorithm_description')}
                  </Typography>
                </Box>
              </Box>

              {/* Right: total trucks badge */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  bgcolor: enableDarkTheme
                    ? 'rgba(168,85,247,0.12)'
                    : 'rgba(168,85,247,0.06)',
                  border: `1px solid ${
                    enableDarkTheme
                      ? 'rgba(168,85,247,0.5)'
                      : 'rgba(168,85,247,0.3)'
                  }`,
                  borderRadius: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: '#c084fc',
                    lineHeight: 1,
                  }}
                >
                  {estimatedTrucksNeeded}
                </Typography>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                      color: labelColor,
                    }}
                  >
                    {t('cdp_total_trucks_required')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    {t('cdp_across_dumping_points', {
                      count: activeDumpingPoints.length,
                    })}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Site configuration error */}
          {siteError && (
            <Alert
              severity="error"
              sx={{
                borderRadius: 1.5,
                '& .MuiAlert-message': { fontSize: 12 },
              }}
            >
              {siteError}
            </Alert>
          )}

          {/* Mismatch banner */}
          {hasMismatch && (
            <Alert
              severity="info"
              sx={{
                borderRadius: 1.5,
                '& .MuiAlert-message': { fontSize: 12 },
              }}
            >
              {t('cdp_truck_mismatch', {
                total: estimatedTrucksNeeded,
                selected: selectedAssetsCount,
                unit:
                  selectedAssetsCount === 1
                    ? t('cdp_truck_is')
                    : t('cdp_trucks_are'),
              })}
            </Alert>
          )}

          {/* ── ASSET LEVEL INPUT ── */}
          <AssetLevelInput
            assets={assetLevelInputData}
            errors={assetErrors}
            allAssets={ALL_AVAILABLE_ASSETS}
            onChangeAsset={(id, field, value) => {
              setAssetLevelInputData((prev) =>
                prev.map((asset) =>
                  asset.id === id ? { ...asset, [field]: value } : asset
                )
              );
              setAssetErrors({});
            }}
            onDelete={(id) => {
              setAssetLevelInputData((prev) => prev.filter((a) => a.id !== id));
            }}
            setAssets={setAssetLevelInputData}
            selectedShiftNumber={selectedShiftNumber}
            setSelectedShiftNumber={setSelectedShiftNumber}
            selectedAssetIds={selectedAssetIds}
            onToggleAsset={(id) => {
              setSelectedAssetIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                  next.delete(id);
                } else {
                  next.add(id);
                }
                return next;
              });
            }}
          />
        </Box>
      )}
    </>
  );
};
